import React from 'react';
import { WorkIdStatus } from '../utils/workIdStatusConstants';

const STEPS = [
  { key: WorkIdStatus.NEW, label: 'New' },
  { key: WorkIdStatus.APPROVED, label: 'Approved' },
  { key: WorkIdStatus.ASSIGNED, label: 'Assigned' },
  { key: WorkIdStatus.CLOSED, label: 'Closed' },
] as const;

const STEP_ORDER: Record<string, number> = {
  [WorkIdStatus.NEW]: 0,
  [WorkIdStatus.APPROVED]: 1,
  [WorkIdStatus.ASSIGNED]: 2,
  [WorkIdStatus.CLOSED]: 3,
  [WorkIdStatus.CLOSED_WITHOUT_ACTION]: 3,
  [WorkIdStatus.CANCELLED]: -1,
  [WorkIdStatus.MERGED]: -1,
};

const CIRCLE_DIAMETER = 30;
const STATUS_CONTAINER_WIDTH = 90;

interface WorkIdStatusProgressProps {
  status: string;
}

const WorkIdStatusProgress: React.FC<WorkIdStatusProgressProps> = ({ status }) => {
  const currentIndex = STEP_ORDER[status?.toUpperCase()] ?? -1;
  const totalStatuses = STEPS.length;

  return (
    <div className="tw-relative tw-mb-4" style={{ width: '100%' }}>
      {/* Progress line background */}
      <div
        className="tw-flex tw-items-center"
        style={{
          position: 'absolute',
          top: '15px',
          left: `${STATUS_CONTAINER_WIDTH / 2}px`,
          right: `${STATUS_CONTAINER_WIDTH / 2}px`,
          height: '2px',
          zIndex: 1,
        }}
      >
        <div style={{ width: '100%', height: '2px', backgroundColor: '#E0E0E0' }} />
      </div>
      {/* Progress line foreground (green) */}
      <div
        className="tw-flex tw-items-center"
        style={{
          position: 'absolute',
          top: '15px',
          left: `${STATUS_CONTAINER_WIDTH / 2}px`,
          width: `calc((100% - ${STATUS_CONTAINER_WIDTH}px) * ${Math.max(0, currentIndex)} / ${totalStatuses - 1})`,
          height: '2px',
          zIndex: 2,
          transition: 'width 0.3s ease-in-out',
        }}
      >
        <div style={{ width: '100%', height: '2px', backgroundColor: '#28a745' }} />
      </div>
      {/* Status circles */}
      <div
        className="tw-flex tw-justify-between tw-items-center"
        style={{ position: 'relative', zIndex: 3 }}
      >
        {STEPS.map((step, index) => {
          const isCompleted = index <= currentIndex;
          const u = status?.toUpperCase() || '';
          const isCurrent =
            step.key === u ||
            (step.key === WorkIdStatus.CLOSED && u === WorkIdStatus.CLOSED_WITHOUT_ACTION);
          return (
            <div
              key={step.key}
              className="tw-flex tw-flex-col tw-items-center"
              style={{
                minWidth: `${STATUS_CONTAINER_WIDTH}px`,
                maxWidth: `${STATUS_CONTAINER_WIDTH}px`,
                flex: `0 1 ${STATUS_CONTAINER_WIDTH}px`,
              }}
            >
              <div
                className="tw-rounded-full tw-flex tw-items-center tw-justify-center"
                style={{
                  width: `${CIRCLE_DIAMETER}px`,
                  height: `${CIRCLE_DIAMETER}px`,
                  backgroundColor: isCompleted ? '#28a745' : '#E0E0E0',
                  color: 'white',
                  marginBottom: '8px',
                  transition: 'background-color 0.3s ease-in-out',
                  flexShrink: 0,
                }}
              >
                {isCompleted && <i className="bi bi-check2" />}
              </div>
              <span
                className="tw-text-sm"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  color: isCurrent ? '#28a745' : '#6c757d',
                  fontSize: '12px',
                  lineHeight: 1.1,
                  height: '28px',
                  marginTop: '2px',
                  overflow: 'hidden',
                }}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkIdStatusProgress;
